import React, { useMemo } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { BASE_OPTIONS, EJE_X_BASE, EJE_Y_BASE } from '../../Recepcion/reportes/chartBase';
import { COLOR, FUENTE } from '../../Recepcion/reportes/tokens';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const etiquetaMes = (clave) => {
    const [anio, mes] = String(clave).split('-');
    return `${MESES[Number(mes) - 1] || mes} ${String(anio).slice(2)}`;
};

/**
 * Evolución mes a mes: el corte que pidió el estudio.
 *
 * Idas y presentaciones van juntas porque no son lo mismo —una ida presenta varias cosas— y verlas
 * separadas esconde justamente eso.
 *
 * "Entraron a ANSES" incluye lo que el organismo ya resolvió, no solo lo que hoy figura como
 * ingresado: el resultado se pisa cuando llega la resolución, así que contando solo INGRESADO la
 * barra verde de un mes cerrado se achicaba sola con el tiempo.
 *
 * Acá había una cuarta serie, "Ausencias", que era siempre cero: la cita de una ida a ANSES nunca
 * llega a NO_ASISTIO porque el scheduler de ausentismo solo cierra turnos de modalidad GENERAL.
 * Una serie plana en cero ocupando un cuarto de cada mes decía "nadie falta nunca", que es
 * distinto de "no se está midiendo".
 *
 * @param {{data: Array<{mes, turnos, presentaciones, entraronAAnses}>}} props
 */
const EvolucionMensualChart = ({ data }) => {
    const filas = useMemo(() => data || [], [data]);

    const chartData = {
        labels: filas.map((f) => etiquetaMes(f.mes)),
        datasets: [
            { label: 'Turnos en ANSES', data: filas.map((f) => f.turnos), backgroundColor: COLOR.azulClaro, borderRadius: 5, barThickness: 18 },
            { label: 'Presentaciones', data: filas.map((f) => f.presentaciones), backgroundColor: COLOR.azul, borderRadius: 5, barThickness: 18 },
            { label: 'Entraron a ANSES', data: filas.map((f) => f.entraronAAnses), backgroundColor: COLOR.verde, borderRadius: 5, barThickness: 18 },
        ],
    };

    const options = {
        ...BASE_OPTIONS,
        scales: {
            x: EJE_X_BASE,
            y: { ...EJE_Y_BASE, beginAtZero: true, ticks: { ...EJE_Y_BASE.ticks, precision: 0 } },
        },
    };

    return (
        <Card variant="outlined" sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: 'none', padding: '18px 20px' }}>
            <Typography sx={{ fontFamily: FUENTE, fontSize: 15, fontWeight: 700, color: COLOR.negro, marginBottom: '2px' }}>
                Evolución mensual
            </Typography>
            <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                Cuánto se fue a ANSES cada mes, cuánto se presentó y cuánto entró. Lo que entró incluye
                lo que el organismo ya resolvió.
            </Typography>
            {filas.length === 0 ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisFrio, padding: '32px 0', textAlign: 'center' }}>
                    No hay turnos en ANSES en el período.
                </Typography>
            ) : (
                <Box sx={{ height: 280 }}>
                    <Bar data={chartData} options={options} />
                </Box>
            )}
        </Card>
    );
};

export default EvolucionMensualChart;
